import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Play, Pause, Volume2, Settings, Loader2, Trash2, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import apiClient from '../utils/apiClient';
import CustomVoiceUpload from './CustomVoiceUpload';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const VoiceSettings = ({ voices, onVoicesUpdate, trialStatus, customVoiceOverride = false }) => {
  const [selectedVoice, setSelectedVoice] = useState(voices[0]?.voice_id || '');
  const [testText, setTestText] = useState('Welcome to our store! This is a sample of how your product descriptions will sound with this voice.');
  const [voiceSettings, setVoiceSettings] = useState({
    stability: 0.7,
    similarity_boost: 0.8,
    style: 0.0,
    use_speaker_boost: true
  });
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [deletingVoiceId, setDeletingVoiceId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);

  // Check custom voice trial status — both bypassed when custom_voice_override flag is on
  const isCustomVoiceTrialExpired = !customVoiceOverride && trialStatus?.trial_active &&
    trialStatus?.custom_voice_trial?.expired;

  const hasUsedTrialCustomVoice = !customVoiceOverride && trialStatus?.trial_active &&
    trialStatus?.custom_voice_trial?.custom_voice_created;

  const stopAllAudio = () => {
    // Stop test audio
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setPlaying(false);
    }
    
    // Stop preview audio
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      setPreviewAudio(null);
      setPlayingVoiceId(null);
    }
  };

  const handlePreviewVoice = (voiceId, previewUrl) => {
    // If this voice is already playing, pause it
    if (playingVoiceId === voiceId && previewAudio) {
      previewAudio.pause();
      setPlayingVoiceId(null);
      setPreviewAudio(null);
      return;
    }

    // Stop any currently playing audio
    stopAllAudio();

    // Create and play new preview
    const audio = new Audio(previewUrl);
    audio.onended = () => {
      setPlayingVoiceId(null);
      setPreviewAudio(null);
    };
    audio.onerror = () => {
      toast.error('Failed to play preview');
      setPlayingVoiceId(null);
      setPreviewAudio(null);
    };

    setPreviewAudio(audio);
    setPlayingVoiceId(voiceId);
    audio.play();
  };

  const handleTestVoice = async () => {
    if (!selectedVoice || !testText.trim()) {
      toast.error('Please select a voice and enter test text');
      return;
    }

    setGenerating(true);
    
    try {
      const response = await apiClient.post('/generate-audio', {
        text: testText,
        voice_id: selectedVoice,
        ...voiceSettings
      });

      // Stop any currently playing audio
      stopAllAudio();

      // Create and play new audio
      const audio = new Audio(response.data.audio_url);
      audio.onended = () => setPlaying(false);
      audio.onerror = () => {
        toast.error('Failed to play audio');
        setPlaying(false);
      };
      
      setCurrentAudio(audio);
      audio.play();
      setPlaying(true);
      
      toast.success('Voice test generated successfully!');
      
    } catch (error) {
      console.error('Error testing voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate voice test');
    } finally {
      setGenerating(false);
    }
  };

  const handleStopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setPlaying(false);
    }
  };

  const handleVoiceCreated = (newVoice) => {
    toast.success('Custom voice created! Refreshing voices...');
    // Trigger parent component to refresh voices
    if (onVoicesUpdate) {
      onVoicesUpdate();
    }
  };

  const handleDeleteCustomVoice = async () => {
    if (!voiceToDelete) return;

    setDeletingVoiceId(voiceToDelete.custom_voice_id);
    setShowDeleteDialog(false);

    try {
      await apiClient.delete(`/voices/custom/${voiceToDelete.custom_voice_id}`);
      
      toast.success('Custom voice deleted successfully');
      
      // If the deleted voice was selected, clear selection
      if (selectedVoice === voiceToDelete.voice_id) {
        const remainingVoices = voices.filter(v => v.voice_id !== voiceToDelete.voice_id);
        setSelectedVoice(remainingVoices[0]?.voice_id || '');
      }
      
      // Trigger parent component to refresh voices
      if (onVoicesUpdate) {
        onVoicesUpdate();
      }
    } catch (error) {
      console.error('Error deleting custom voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete custom voice');
    } finally {
      setDeletingVoiceId(null);
      setVoiceToDelete(null);
    }
  };

  const selectedVoiceData = voices.find(v => v.voice_id === selectedVoice);
  
  // Separate custom and standard voices
  const customVoices = voices.filter(v => v.is_custom);
  const standardVoices = voices.filter(v => !v.is_custom);

  return (
    <div className="space-y-6">
      {/* Custom Voice Override Badge */}
      {customVoiceOverride && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-indigo-600" />
            <div>
              <p className="text-sm font-semibold text-indigo-900">Custom Voice Override (Custom Plan)</p>
              <p className="text-xs text-indigo-700">Voice trial limits are bypassed — create as many custom voices as your plan allows.</p>
            </div>
          </div>
        </div>
      )}

      {/* Trial Status Info for Custom Voice — hidden when custom_voice_override is active */}
      {!customVoiceOverride && trialStatus?.trial_active && (
        <div className={`rounded-lg p-4 border ${
          isCustomVoiceTrialExpired
            ? 'bg-orange-50 border-orange-200'
            : hasUsedTrialCustomVoice
              ? 'bg-green-50 border-green-200'
              : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-3">
            {isCustomVoiceTrialExpired ? (
              <>
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm font-semibold text-orange-900">
                    Custom Voice Trial Expired
                  </p>
                  <p className="text-xs text-orange-700">
                    Upgrade to Professional ($39.99/mo) to create unlimited custom voices
                  </p>
                </div>
              </>
            ) : hasUsedTrialCustomVoice ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-900">
                    Trial Custom Voice Created (1/1)
                  </p>
                  <p className="text-xs text-green-700">
                    Your trial voice is permanent. Upgrade for unlimited custom voices.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Clock className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">
                    Custom Voice Trial: {trialStatus.custom_voice_trial.days_remaining} days remaining (0/1 used)
                  </p>
                  <p className="text-xs text-blue-700">
                    Create 1 custom voice free during your trial. It becomes permanent when you upgrade!
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Custom Voice Upload */}
      <Card className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-white">
        <CardHeader>
          <CardTitle className="text-lg flex items-center space-x-2">
            <span>Custom Voice Cloning</span>
            <Badge variant="secondary" className="ml-2">New</Badge>
          </CardTitle>
          <CardDescription>
            Upload your own voice recording to create a unique AI voice clone for your products
            {!customVoiceOverride && hasUsedTrialCustomVoice && !trialStatus?.on_paid_plan && (
              <span className="block mt-1 text-orange-600 font-medium">
                Trial limit reached (1/1). Upgrade to create more voices.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CustomVoiceUpload 
            onVoiceCreated={handleVoiceCreated} 
            disabled={hasUsedTrialCustomVoice || isCustomVoiceTrialExpired}
          />
        </CardContent>
      </Card>

      {/* Custom Voices Section */}
      {customVoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Custom Voices</CardTitle>
            <CardDescription>
              AI-generated voice clones from your uploaded samples
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customVoices.map((voice) => (
                <div
                  key={voice.voice_id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all relative ${
                    selectedVoice === voice.voice_id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedVoice(voice.voice_id)}
                  data-testid={`voice-option-${voice.voice_id}`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-purple-100 text-purple-600">
                        {voice.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{voice.name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200">
                          Custom
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    {voice.preview_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreviewVoice(voice.voice_id, voice.preview_url);
                        }}
                        data-testid={`preview-voice-${voice.voice_id}`}
                      >
                        {playingVoiceId === voice.voice_id ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Preview
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVoiceToDelete(voice);
                        setShowDeleteDialog(true);
                      }}
                      disabled={deletingVoiceId === voice.custom_voice_id}
                      data-testid={`delete-voice-${voice.voice_id}`}
                    >
                      {deletingVoiceId === voice.custom_voice_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Voice Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Standard Voices</CardTitle>
          <CardDescription>
            Professional AI voices from ElevenLabs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {standardVoices.map((voice) => (
              <div
                key={voice.voice_id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedVoice === voice.voice_id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedVoice(voice.voice_id)}
                data-testid={`voice-option-${voice.voice_id}`}
              >
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-indigo-100 text-indigo-600">
                      {voice.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{voice.name}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {voice.category}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {voice.preview_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewVoice(voice.voice_id, voice.preview_url);
                    }}
                    data-testid={`preview-voice-${voice.voice_id}`}
                  >
                    {playingVoiceId === voice.voice_id ? (
                      <>
                        <Pause className="h-3 w-3 mr-1" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3 mr-1" />
                        Preview
                      </>
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Voice Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Voice Parameters</CardTitle>
          <CardDescription>
            Fine-tune the voice characteristics for optimal results
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Stability */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">Stability</label>
                <span className="text-sm text-gray-600">{voiceSettings.stability}</span>
              </div>
              <Slider
                value={[voiceSettings.stability]}
                onValueChange={([value]) => setVoiceSettings(prev => ({ ...prev, stability: value }))}
                max={1}
                min={0}
                step={0.1}
                className="w-full"
                data-testid="stability-slider"
              />
              <p className="text-xs text-gray-500">
                Higher values make the voice more stable but less expressive
              </p>
            </div>

            {/* Similarity Boost */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">Similarity Boost</label>
                <span className="text-sm text-gray-600">{voiceSettings.similarity_boost}</span>
              </div>
              <Slider
                value={[voiceSettings.similarity_boost]}
                onValueChange={([value]) => setVoiceSettings(prev => ({ ...prev, similarity_boost: value }))}
                max={1}
                min={0}
                step={0.1}
                className="w-full"
                data-testid="similarity-slider"
              />
              <p className="text-xs text-gray-500">
                Higher values make the voice more similar to the original
              </p>
            </div>

            {/* Style */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">Style</label>
                <span className="text-sm text-gray-600">{voiceSettings.style}</span>
              </div>
              <Slider
                value={[voiceSettings.style]}
                onValueChange={([value]) => setVoiceSettings(prev => ({ ...prev, style: value }))}
                max={1}
                min={0}
                step={0.1}
                className="w-full"
                data-testid="style-slider"
              />
              <p className="text-xs text-gray-500">
                Higher values add more style and emotion to the voice
              </p>
            </div>

            {/* Speaker Boost */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Speaker Boost</label>
                <Button
                  variant={voiceSettings.use_speaker_boost ? "default" : "outline"}
                  size="sm"
                  onClick={() => setVoiceSettings(prev => ({ ...prev, use_speaker_boost: !prev.use_speaker_boost }))}
                  data-testid="speaker-boost-toggle"
                >
                  {voiceSettings.use_speaker_boost ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Enhances the voice clarity and volume
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Voice Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Voice</CardTitle>
          <CardDescription>
            Test how your selected voice and settings will sound
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Test Text</label>
            <Textarea
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Enter text to test the voice..."
              rows={3}
              data-testid="test-text-input"
            />
          </div>

          {selectedVoiceData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                    {selectedVoiceData.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-gray-900">{selectedVoiceData.name}</p>
                  <p className="text-sm text-gray-600">{selectedVoiceData.category}</p>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p>Stability: {voiceSettings.stability} | Similarity: {voiceSettings.similarity_boost}</p>
                <p>Style: {voiceSettings.style} | Speaker Boost: {voiceSettings.use_speaker_boost ? 'On' : 'Off'}</p>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <Button
              onClick={handleTestVoice}
              disabled={generating || playing || !selectedVoice || !testText.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="test-voice-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Test Voice
                </>
              )}
            </Button>
            
            {playing && (
              <Button
                variant="outline"
                onClick={handleStopAudio}
                data-testid="stop-audio-btn"
              >
                <Pause className="h-4 w-4 mr-2" />
                Stop
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span>Delete Custom Voice?</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{voiceToDelete?.name}&quot;? This action cannot be undone.
              The voice will be removed from ElevenLabs and your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomVoice}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VoiceSettings;
